/**
 * A COPY FIELD THAT UNDERLINES ITS OWN TYPOS (2026-08-22, Jack picked C4).
 *
 * Red wavy underline under the misspelled word, click it for a Word-style menu:
 * suggestions, then Ignore once, then Add to dictionary.
 *
 * ── WHY THERE IS A SECOND COPY OF THE TEXT ────────────────────────────────────
 * A `<textarea>` cannot style part of its own content. The only way to underline
 * one word is the mirror trick: a div behind the textarea holding the same text at
 * the same metrics, with the flagged words wrapped in spans that carry the wavy
 * underline. The textarea sits on top with a transparent background, so you read
 * the textarea's text and see the mirror's decoration through it.
 *
 * That makes METRIC PARITY the whole ballgame — one pixel of difference in padding
 * or letter-spacing and every underline drifts off its word by the end of the
 * line. The metrics are COPIED FROM THE LIVE ELEMENT with `getComputedStyle`
 * rather than re-declared in CSS, because the real typography lives in
 * element-qualified rules (`textarea.ech-h`, `textarea.ech-b`) that a div can
 * never match. Copying is the only approach that stays correct when those rules
 * change.
 *
 * ── HIT TESTING IS DONE ON THE CARET, NOT THE SPAN ────────────────────────────
 * The textarea is on top, so it swallows the clicks — the mirror spans are
 * unclickable by construction (`pointer-events:none`, which they must keep, or
 * they would steal typing). So a click is resolved the way a text editor does it:
 * read `selectionStart` after the click and find the issue whose [start,end)
 * contains it. This is more robust than any geometric hit test, and it means
 * arrow-keying into a word opens the menu too.
 *
 * ── THE MENU IS CLAMPED TO THE RAIL ──────────────────────────────────────────
 * `.esinsp` is `width:330px; overflow-x:hidden` (290px under 1180px wide), so a
 * menu opening from a word near the right edge would be cut off with no way to
 * scroll to it. `place()` measures the word and flips the menu left when it would
 * overflow, and opens upward when there is no room below. This is the one real
 * cost of the floating variants and it is handled here, once.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { stripBreaks, keepBulletBreaks, bulletEnter } from "../services/echBreaks";
import {
  loadSpell,
  spellCheckText,
  spellSubscribe,
  spellReady,
  spellLearn,
  applySuggestion,
  type SpellIssue,
} from "../services/spell";

/** the properties that decide where a glyph lands; anything else is irrelevant */
const METRICS = [
  "boxSizing", "width", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
  "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
  "fontFamily", "fontSize", "fontWeight", "fontStyle", "fontVariant", "lineHeight",
  "letterSpacing", "wordSpacing", "textAlign", "textIndent", "textTransform",
  "whiteSpace", "overflowWrap", "wordBreak", "tabSize",
] as const;

type Props = {
  value: string;
  onChange: (next: string) => void;
  /** the real field classes — `ech-h`, `ech-b ech-grow`, `rep-in`… */
  className?: string;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
  readOnly?: boolean;
  /** drop line breaks: Enter / Shift+Enter do nothing, a pasted break becomes a space (2026-10-30) */
  noBreaks?: boolean;
  /** with noBreaks: Enter on a • line starts the next bullet, and breaks before • lines are kept (2026-10-30) */
  bulletBreaks?: boolean;
};

export function SpellField({
  value,
  onChange,
  className,
  rows,
  placeholder,
  maxLength,
  readOnly,
  noBreaks,
  bulletBreaks,
}: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const mirRef = useRef<HTMLDivElement | null>(null);

  const [issues, setIssues] = useState<SpellIssue[]>([]);
  const [openAt, setOpenAt] = useState<number | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number; up: boolean } | null>(null);
  /** dismissed for this field, this session only — never persisted */
  const ignored = useRef<Set<string>>(new Set());

  const vRef = useRef(value);
  vRef.current = value;

  /* ─────────────────────────────────────────────────── the check */

  const run = useCallback(() => {
    const next = spellReady()
      ? spellCheckText(vRef.current).filter((i) => !ignored.current.has(i.word.toLowerCase()))
      : [];
    setIssues((prev) => (prev.length === 0 && next.length === 0 ? prev : next));
  }, []);

  useEffect(() => {
    let live = true;
    loadSpell().then(() => {
      if (live) run();
    });
    const off = spellSubscribe(() => {
      if (live) run();
    });
    return () => {
      live = false;
      off();
    };
  }, [run]);

  useEffect(() => {
    if (!spellReady()) return;
    const t = window.setTimeout(run, 400);
    return () => window.clearTimeout(t);
  }, [value, run]);

  /* editing invalidates every offset, so any open menu must go */
  useEffect(() => setOpenAt(null), [value]);

  /* ─────────────────────────────────────────── metric parity */

  const syncMetrics = useCallback(() => {
    const ta = taRef.current;
    const mir = mirRef.current;
    if (!ta || !mir) return;
    const cs = window.getComputedStyle(ta);
    for (const k of METRICS) {
      (mir.style as unknown as Record<string, string>)[k] = cs[k as keyof CSSStyleDeclaration] as string;
    }
    /* the border is copied as WIDTH only and drawn transparent: the mirror must
       occupy the same box as the textarea, but must not paint a second border on
       top of it */
    mir.style.borderStyle = "solid";
    mir.style.borderColor = "transparent";
    mir.style.height = cs.height;
    mir.scrollTop = ta.scrollTop;
  }, []);

  useLayoutEffect(() => {
    syncMetrics();
  }, [syncMetrics, value, className, issues.length]);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta || typeof ResizeObserver === "undefined") return;
    /* the field grows with its text (field-sizing:content on .ech-grow) and the
       rail itself changes width at 1180px — both move every glyph */
    const ro = new ResizeObserver(() => syncMetrics());
    ro.observe(ta);
    return () => ro.disconnect();
  }, [syncMetrics]);

  /* ───────────────────────────────────────────── the segments */

  const segments = useMemo(() => {
    const s = String(value ?? "");
    if (!issues.length) return [s];
    const out: Array<string | { w: string; at: number }> = [];
    let cur = 0;
    for (const i of issues) {
      if (i.start > cur) out.push(s.slice(cur, i.start));
      out.push({ w: s.slice(i.start, i.end), at: i.start });
      cur = i.end;
    }
    if (cur < s.length) out.push(s.slice(cur));
    return out;
  }, [value, issues]);

  /* ────────────────────────────────────────── open and place */

  const place = useCallback((at: number) => {
    const wrap = wrapRef.current;
    const mir = mirRef.current;
    if (!wrap || !mir) return;
    const span = mir.querySelector<HTMLElement>('[data-at="' + at + '"]');
    if (!span) return;
    const w = wrap.getBoundingClientRect();
    const r = span.getBoundingClientRect();

    const MENU_W = 168;
    const MENU_H = 132;
    let left = r.left - w.left;
    /* flip left rather than run past the rail — .esinsp clips horizontally, so an
       overflowing menu is simply unreachable */
    if (left + MENU_W > w.width) left = Math.max(0, w.width - MENU_W);

    const belowRoom = window.innerHeight - r.bottom;
    const up = belowRoom < MENU_H && r.top > MENU_H;
    const top = up ? r.top - w.top : r.bottom - w.top;
    setPos({ left, top, up });
  }, []);

  const resolveCaret = useCallback(() => {
    const ta = taRef.current;
    if (!ta || readOnly) return;
    const c = ta.selectionStart ?? -1;
    if (c < 0) return;
    const hit = issues.find((i) => c >= i.start && c <= i.end);
    if (!hit) {
      setOpenAt(null);
      return;
    }
    setOpenAt(hit.start);
    place(hit.start);
  }, [issues, place, readOnly]);

  /* close on anything that could move the word out from under the menu */
  useEffect(() => {
    if (openAt == null) return;
    const shut = () => setOpenAt(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();   /* the Studio's window Escape closes the whole overlay */
        setOpenAt(null);
        taRef.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpenAt(null);
    };
    window.addEventListener("scroll", shut, true);
    window.addEventListener("resize", shut);
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("scroll", shut, true);
      window.removeEventListener("resize", shut);
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown);
    };
  }, [openAt]);

  const open = openAt != null ? issues.find((i) => i.start === openAt) : undefined;

  const accept = (iss: SpellIssue, word: string) => {
    const next = applySuggestion(value, iss, word);
    onChange(next);
    setOpenAt(null);
    /* put the caret after the corrected word so typing continues where it was */
    const to = iss.start + word.length;
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      ta.focus();
      try {
        ta.setSelectionRange(to, to);
      } catch {
        /* detached */
      }
    });
  };

  return (
    <div className="spf" ref={wrapRef}>
      <div className="spf-mir" ref={mirRef} aria-hidden="true">
        {segments.map((seg, n) =>
          typeof seg === "string" ? (
            <React.Fragment key={n}>{seg}</React.Fragment>
          ) : (
            <span className="spf-bad" data-at={seg.at} key={n}>
              {seg.w}
            </span>
          ),
        )}
        {/* a textarea keeps a trailing newline's line, a div collapses it */}
        {"​"}
      </div>

      <textarea
        ref={taRef}
        className={"spf-ta" + (className ? " " + className : "")}
        rows={rows}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        readOnly={readOnly}
        /* ours replaces the browser's — native would flag Lennar, homesite and
           every community name, which is the noise this whole service exists to
           remove */
        spellCheck={false}
        onChange={(e) => onChange(!noBreaks ? e.target.value : bulletBreaks ? keepBulletBreaks(e.target.value) : stripBreaks(e.target.value))}
        onKeyDown={(e) => {
          if (!noBreaks || e.key !== "Enter") return;
          e.preventDefault();
          if (!bulletBreaks) return;
          const ta = e.currentTarget;
          const r = bulletEnter(ta.value, ta.selectionStart ?? ta.value.length);
          if (!r) return;
          onChange(r.value);
          requestAnimationFrame(() => {
            const el = taRef.current;
            if (!el) return;
            try { el.setSelectionRange(r.caret, r.caret); } catch { /* detached */ }
          });
        }}
        onScroll={() => {
          if (mirRef.current && taRef.current) mirRef.current.scrollTop = taRef.current.scrollTop;
        }}
        onClick={resolveCaret}
        onKeyUp={(e) => {
          if (e.key.indexOf("Arrow") === 0 || e.key === "Home" || e.key === "End") resolveCaret();
        }}
      />

      {open && pos && (
        <div
          className={"spf-menu" + (pos.up ? " up" : "")}
          style={{ left: pos.left, top: pos.top }}
          role="menu"
        >
          {open.suggestions.length ? (
            open.suggestions.map((s) => (
              <button
                type="button"
                role="menuitem"
                className="spf-mi s"
                key={s}
                onClick={() => accept(open, s)}
              >
                {s}
              </button>
            ))
          ) : (
            <div className="spf-mi none">No suggestion</div>
          )}
          <div className="spf-sep" />
          <button
            type="button"
            role="menuitem"
            className="spf-mi dim"
            onClick={() => {
              ignored.current.add(open.word.toLowerCase());
              setOpenAt(null);
              run();
            }}
          >
            Ignore once
          </button>
          <button
            type="button"
            role="menuitem"
            className="spf-mi dim"
            onClick={() => {
              spellLearn(open.word);
              setOpenAt(null);
            }}
          >
            Add to dictionary
          </button>
        </div>
      )}
    </div>
  );
}

export default SpellField;
